import { motion } from "framer-motion";
import { AlertCircle, Antenna, Info, Network, Radio } from "lucide-react";
import { StatusBadge, TechCard } from "../components/tech";

const pageVariants = {
  initial: { opacity: 0 },
  animate: { opacity: 1 },
  exit: { opacity: 0 },
};

export default function WiresXPage() {
  return (
    <motion.div
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      transition={{ duration: 0.5 }}
      className="py-12 px-4"
    >
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-accent-primary/10 mb-4">
            <Network className="w-8 h-8 text-accent-primary" />
          </div>
          <h1 className="text-4xl md:text-5xl font-display font-bold text-text-primary mb-3">
            KQ4TNV WIRES-X Simplex Node
          </h1>
          <p className="text-lg text-text-secondary max-w-2xl mx-auto">
            Internet-linked WIRES-X access via simplex node (not a repeater)
          </p>
          <div className="mt-4">
            <StatusBadge status="offline" label="Node Offline" />
          </div>
        </motion.div>

        {/* Outage Alert */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="mb-8"
        >
          <div className="glass-morphism p-6 rounded-lg border border-accent-secondary/30 bg-accent-secondary/10">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-6 h-6 text-accent-secondary flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-semibold text-accent-secondary text-lg mb-2">
                  Temporary Service Outage
                </h3>
                <p className="text-sm text-text-secondary mb-2">
                  The WIRES-X node is currently offline due to the glacial storm
                  that impacted the National Capital Region before the antenna
                  could be raised.
                </p>
                <p className="text-sm text-text-secondary">
                  <strong className="text-text-primary">
                    Expected Return:
                  </strong>{" "}
                  Service is not expected to resume for at least another 10
                  days. Updates will be posted here as the situation develops.
                </p>
              </div>
            </div>
          </div>
        </motion.div>

        {/* On-Air Details */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-8"
        >
          <TechCard>
            <div className="flex items-center mb-6">
              <div className="bg-accent-primary/20 p-2 rounded-lg mr-3">
                <Radio className="w-5 h-5 text-accent-primary" />
              </div>
              <h2 className="text-2xl font-display font-semibold text-text-primary">
                On-Air Details
              </h2>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div>
                  <span className="text-sm text-text-secondary">Frequency</span>
                  <p className="text-2xl font-mono font-bold text-accent-primary">
                    145.785 MHz
                  </p>
                </div>
                <div>
                  <span className="text-sm text-text-secondary">Mode</span>
                  <p className="text-lg font-semibold text-text-primary">
                    C4FM DN (Digital Narrow)
                  </p>
                </div>
                <div>
                  <span className="text-sm text-text-secondary">Node ID</span>
                  <p className="text-lg font-mono font-semibold text-text-primary">
                    KQ4TNV-ND
                  </p>
                </div>
              </div>
              <div className="space-y-4">
                <div>
                  <span className="text-sm text-text-secondary">Type</span>
                  <p className="text-lg font-semibold text-text-primary">
                    Simplex WIRES-X Node
                  </p>
                  <p className="text-sm text-text-secondary">
                    HRI-200 + FTM-200
                  </p>
                </div>
                <div>
                  <span className="text-sm text-text-secondary">Location</span>
                  <p className="text-lg font-semibold text-text-primary">
                    Stafford, VA
                  </p>
                </div>
                <div>
                  <span className="text-sm text-text-secondary">DTMF ID</span>
                  <p className="text-lg font-mono font-semibold text-text-primary">
                    99012
                  </p>
                </div>
              </div>
            </div>

            {/* WIRES-X Node & Room Information */}
            <div className="mt-6 pt-6 border-t border-border-primary">
              <h3 className="text-lg font-display font-semibold text-text-primary mb-4">
                Active WIRES-X Room
              </h3>
              <div className="p-4 bg-bg-elevated/50 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-text-secondary">Room ID</span>
                  <span className="text-xl font-mono font-bold text-accent-primary">
                    09012
                  </span>
                </div>
                <div className="mt-4 flex flex-wrap gap-3">
                  <a
                    href="https://www.yaesu.com/jp/en/wires-x/id/active_node.php"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-accent-primary hover:text-accent-secondary transition-colors underline"
                  >
                    View Active Nodes
                  </a>
                  <span className="text-text-secondary">•</span>
                  <a
                    href="https://www.yaesu.com/jp/en/wires-x/id/id_usa.php"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-accent-primary hover:text-accent-secondary transition-colors underline"
                  >
                    USA Room Directory
                  </a>
                </div>
              </div>
            </div>
          </TechCard>
        </motion.div>

        {/* How many users can connect? */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-8"
        >
          <TechCard>
            <div className="flex items-center mb-6">
              <div className="bg-accent-primary/20 p-2 rounded-lg mr-3">
                <Info className="w-5 h-5 text-accent-primary" />
              </div>
              <h2 className="text-2xl font-display font-semibold text-text-primary">
                How many users can connect at once?
              </h2>
            </div>

            <div className="space-y-4">
              <div className="p-4 bg-bg-elevated/50 rounded-lg">
                <h3 className="font-semibold text-text-primary mb-2">
                  Locally (RF on 145.785 / DG-ID 00):
                </h3>
                <ul className="space-y-2 text-sm text-text-secondary">
                  <li className="flex items-start gap-2">
                    <span className="text-accent-primary mt-1">•</span>
                    <span>
                      <strong className="text-text-primary">
                        Any number of stations can monitor
                      </strong>{" "}
                      <em>when the node is transmitting</em> (i.e., when audio
                      is coming from the connected WIRES-X room down to RF).
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-accent-primary mt-1">•</span>
                    <span>
                      <strong className="text-text-primary">
                        Only one station can transmit at a time
                      </strong>
                      . This is a{" "}
                      <strong className="text-text-primary">
                        single simplex channel
                      </strong>
                      , so local users must take turns. If two stations key up
                      at once, it's normal simplex contention/doubles.
                    </span>
                  </li>
                </ul>
              </div>

              <div className="p-4 bg-bg-elevated/50 rounded-lg">
                <h3 className="font-semibold text-text-primary mb-2">
                  On the WIRES-X network / room side:
                </h3>
                <ul className="space-y-2 text-sm text-text-secondary">
                  <li className="flex items-start gap-2">
                    <span className="text-accent-primary mt-1">•</span>
                    <span>
                      The WIRES-X room sees{" "}
                      <strong className="text-text-primary">
                        this gateway as one node connection
                      </strong>{" "}
                      (the HRI-200/FTM-200 node).
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-accent-primary mt-1">•</span>
                    <span>
                      Multiple nodes can be connected to the same room at the
                      same time, but all local RF users in range are effectively{" "}
                      <strong className="text-text-primary">
                        sharing this single gateway
                      </strong>
                      .
                    </span>
                  </li>
                </ul>
              </div>

              <div className="p-4 bg-accent-primary/10 rounded-lg border border-accent-primary/30">
                <h3 className="font-semibold text-text-primary mb-2">
                  Bottom line:
                </h3>
                <p className="text-sm text-text-secondary mb-3">
                  The practical "limit" locally is{" "}
                  <strong className="text-text-primary">
                    1 talker at a time
                  </strong>{" "}
                  on the RF channel (with many possible listeners). If you need
                  true{" "}
                  <strong className="text-text-primary">
                    duplex/repeater-style
                  </strong>{" "}
                  local coverage, use a{" "}
                  <strong className="text-text-primary">
                    WIRES-X-capable repeater
                  </strong>{" "}
                  that you can access in your area.
                </p>
              </div>
            </div>
          </TechCard>
        </motion.div>

        {/* How to Connect - FT-70D Example */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-8"
        >
          <TechCard>
            <div className="flex items-center mb-6">
              <div className="bg-accent-primary/20 p-2 rounded-lg mr-3">
                <Antenna className="w-5 h-5 text-accent-primary" />
              </div>
              <h2 className="text-2xl font-display font-semibold text-text-primary">
                How to Connect (FT-70D Example)
              </h2>
            </div>

            <div className="space-y-6">
              <div className="prose prose-invert max-w-none">
                <p className="text-text-secondary mb-4">
                  Follow these steps to connect your Yaesu FT-70D to the WIRES-X
                  node:
                </p>
              </div>

              <div className="space-y-4">
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-accent-primary/20 flex items-center justify-center text-accent-primary font-bold">
                    1
                  </div>
                  <div>
                    <p className="text-text-primary font-semibold">
                      Tune to 145.785 MHz
                    </p>
                    <p className="text-sm text-text-secondary">
                      Set your radio to the simplex frequency
                    </p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-accent-primary/20 flex items-center justify-center text-accent-primary font-bold">
                    2
                  </div>
                  <div>
                    <p className="text-text-primary font-semibold">
                      Set Mode to DN (Digital Narrow)
                    </p>
                    <p className="text-sm text-text-secondary">
                      Ensure you're in C4FM digital mode
                    </p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-accent-primary/20 flex items-center justify-center text-accent-primary font-bold">
                    3
                  </div>
                  <div>
                    <p className="text-text-primary font-semibold">
                      Press [F] then [AMS]
                    </p>
                    <p className="text-sm text-text-secondary">
                      This initiates the WIRES-X connection
                    </p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-accent-primary/20 flex items-center justify-center text-accent-primary font-bold">
                    4
                  </div>
                  <div>
                    <p className="text-text-primary font-semibold">
                      Navigate/Select Rooms in WIRES-X Menu
                    </p>
                    <p className="text-sm text-text-secondary">
                      Use the radio's menu to browse available rooms and
                      reflectors
                    </p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-accent-primary/20 flex items-center justify-center text-accent-primary font-bold">
                    5
                  </div>
                  <div>
                    <p className="text-text-primary font-semibold">
                      Press PTT to Talk
                    </p>
                    <p className="text-sm text-text-secondary">
                      Transmit as you would normally on the selected room
                    </p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-accent-primary/20 flex items-center justify-center text-accent-primary font-bold">
                    6
                  </div>
                  <div>
                    <p className="text-text-primary font-semibold">
                      Disconnect from Room
                    </p>
                    <p className="text-sm text-text-secondary">
                      Press [BAND] to disconnect from the current room
                    </p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-accent-primary/20 flex items-center justify-center text-accent-primary font-bold">
                    7
                  </div>
                  <div>
                    <p className="text-text-primary font-semibold">
                      Exit WIRES-X
                    </p>
                    <p className="text-sm text-text-secondary">
                      Hold [MODE] to exit the WIRES-X system
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </TechCard>
        </motion.div>

        {/* Operating Notes */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-8"
        >
          <TechCard>
            <div className="flex items-center mb-6">
              <div className="bg-accent-primary/20 p-2 rounded-lg mr-3">
                <Info className="w-5 h-5 text-accent-primary" />
              </div>
              <h2 className="text-2xl font-display font-semibold text-text-primary">
                Operating Notes
              </h2>
            </div>

            <div className="space-y-4">
              <div className="p-4 bg-bg-elevated/50 rounded-lg">
                <h3 className="font-semibold text-text-primary mb-2">
                  Simplex Etiquette
                </h3>
                <ul className="space-y-2 text-sm text-text-secondary">
                  <li className="flex items-start gap-2">
                    <span className="text-accent-primary mt-1">•</span>
                    <span>
                      Pause between transmissions to allow others to join
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-accent-primary mt-1">•</span>
                    <span>Keep overs short and concise</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-accent-primary mt-1">•</span>
                    <span>Monitor before transmitting to avoid doubling</span>
                  </li>
                </ul>
              </div>

              <div className="p-4 bg-bg-elevated/50 rounded-lg">
                <h3 className="font-semibold text-text-primary mb-2">
                  Power Settings
                </h3>
                <p className="text-sm text-text-secondary">
                  Node power may vary (Low/Mid/High) based on testing and
                  optimization. Adjust your power appropriately for local
                  coverage.
                </p>
              </div>

              <div className="p-4 bg-bg-elevated/50 rounded-lg">
                <h3 className="font-semibold text-text-primary mb-2">
                  Coverage Expectations
                </h3>
                <p className="text-sm text-text-secondary">
                  Local Stafford area coverage. Range varies with terrain and
                  your station antenna height and quality.
                </p>
              </div>
            </div>
          </TechCard>
        </motion.div>

        {/* Disclaimer */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <div className="glass-morphism p-6 rounded-lg border border-accent-secondary/30">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-accent-secondary flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-semibold text-accent-secondary mb-2">
                  Important Notice
                </h3>
                <p className="text-sm text-text-secondary">
                  This WIRES-X node is provided for amateur radio use and
                  experimentation. It is not intended for emergency
                  communications or critical reliance. Always maintain
                  alternative communication methods.
                </p>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </motion.div>
  );
}
